import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Modal, Form, Badge, InputGroup, Alert } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaImage, FaLayerGroup, FaImages, FaTh, FaSearch } from 'react-icons/fa';
import './BannerAds.css';
import { adSetsApi } from '../../../services/sponsoredAdsApi';
import { bannerAdsApi, uploadBannerFileViaPresignedUrl } from '../../../services/bannerAdsApi';
import SetAlert from '../../../components/SetAlert';
import {
  BANNER_PLACEMENT_KEYS,
  BANNER_PLACEMENT_LABELS,
  emptyPlacementFields,
  normalizePlacementsFromApi,
  buildPlacementsPayload,
  countActivePlacementsFromApi
} from './bannerAdPlacements';

/** S3 object key prefix (bucket chosen server-side when issuing presigned URL) */
const S3_ADS_FOLDER = process.env.REACT_APP_S3_ADS_FOLDER || 'ads';

const BannerAds = () => {
  const [ads, setAds] = useState([]);
  const [adSets, setAdSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [alertMessage, setAlertMessage] = useState({ success: '', error: '' });
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadingPlacementKey, setUploadingPlacementKey] = useState(null);

  const [formData, setFormData] = useState({
    adSetId: '',
    assetName: '',
    caption: '',
    media: '',
    ctaText: '',
    ctaUrl: '',
    priority: 1
  });
  const [placementFields, setPlacementFields] = useState(() => emptyPlacementFields());

  useEffect(() => {
    fetchBannerAds();
    fetchAdSets();
  }, []);

  const fetchBannerAds = async () => {
    setLoading(true);
    try {
      const response = await bannerAdsApi.getAll();
      if (response.statusCode === '10000') {
        const transformed = Array.isArray(response.data)
          ? response.data.map((ad) => ({
              id: ad.id,
              assetName: ad.asset_name,
              caption: ad.caption ?? '',
              media: ad.media,
              ctaText: ad.cta_text ?? '',
              ctaUrl: ad.cta_url,
              priority: ad.priority,
              adSetId: ad.ad_set_id,
              status: ad.status || 'active',
              placements: ad.placements || {},
              createdAt: ad.created_at,
              updatedAt: ad.updated_at
            }))
          : [];
        setAds(transformed);
      } else {
        setAlertMessage((prev) => ({
          ...prev,
          error: response.message || 'Failed to fetch banner ads'
        }));
        setAds([]);
      }
    } catch (e) {
      console.error('Error fetching banner ads:', e);
      setAlertMessage((prev) => ({ ...prev, error: 'Error fetching banner ads' }));
      setAds([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdSets = async () => {
    try {
      const response = await adSetsApi.getAll();
      if (response.statusCode === '10000') {
        const transformed = Array.isArray(response.data)
          ? response.data.map((adSet) => ({
              id: adSet.id,
              adSetName: adSet.ad_set_name,
              brandName: adSet.brand_name
            }))
          : [];
        setAdSets(transformed);
      } else {
        setAdSets([]);
      }
    } catch (e) {
      console.error('Error fetching ad sets:', e);
      setAdSets([]);
    }
  };

  const resetForm = () => {
    setFormData({
      adSetId: '',
      assetName: '',
      caption: '',
      media: '',
      ctaText: '',
      ctaUrl: '',
      priority: 1
    });
    setPlacementFields(emptyPlacementFields());
  };

  const handleShowModal = async (ad = null) => {
    if (ad) {
      setFormData({
        adSetId: ad.adSetId != null ? String(ad.adSetId) : '',
        assetName: ad.assetName,
        caption: ad.caption ?? '',
        media: ad.media,
        ctaText: ad.ctaText ?? '',
        ctaUrl: ad.ctaUrl,
        priority: ad.priority
      });
      setPlacementFields(normalizePlacementsFromApi(ad.placements));
      setSelectedAd(ad);

      try {
        const res = await bannerAdsApi.getById(ad.id);
        if (res.statusCode === '10000' && res.data) {
          const row = res.data;
          setFormData({
            adSetId: row.ad_set_id != null ? String(row.ad_set_id) : '',
            assetName: row.asset_name,
            caption: row.caption ?? '',
            media: row.media,
            ctaText: row.cta_text ?? '',
            ctaUrl: row.cta_url,
            priority: row.priority
          });
          setPlacementFields(normalizePlacementsFromApi(row.placements));
        }
      } catch (e) {
        console.warn('Banner ad refresh skipped:', e);
      }
    } else {
      setSelectedAd(null);
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAd(null);
    resetForm();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePlacementFieldChange = (key, field, value) => {
    setPlacementFields((prev) => {
      const cur = prev[key] || { image_url: '', redirection_url: '', enabled: false };
      return {
        ...prev,
        [key]: { ...cur, [field]: value }
      };
    });
  };

  const handlePlacementEnabledChange = (key, checked) => {
    setPlacementFields((prev) => {
      const cur = prev[key] || { image_url: '', redirection_url: '', enabled: false };
      return {
        ...prev,
        [key]: {
          ...cur,
          enabled: checked,
          ...(checked ? {} : { image_url: '', redirection_url: '' })
        }
      };
    });
  };

  const ensureAssetNameForUpload = () => {
    const name = (formData.assetName || '').trim();
    if (!name) {
      setAlertMessage((prev) => ({
        ...prev,
        error: 'Set Asset name first so uploads use a stable S3 path.',
        success: ''
      }));
      return null;
    }
    return name.replace(/\s+/g, '_');
  };

  const handleDefaultMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const safeName = ensureAssetNameForUpload();
    if (!safeName) return;

    setUploadingMedia(true);
    try {
      const ext = file.name.split('.').pop();
      const objectKey = `${S3_ADS_FOLDER}/${safeName}/default_media.${ext}`;
      const location = await uploadBannerFileViaPresignedUrl(file, objectKey);
      setFormData((prev) => ({ ...prev, media: location }));
      setAlertMessage((prev) => ({ ...prev, success: 'Default image uploaded', error: '' }));
    } catch (err) {
      console.error(err);
      setAlertMessage((prev) => ({
        ...prev,
        error: err?.message || 'Failed to upload default image',
        success: ''
      }));
    } finally {
      setUploadingMedia(false);
      if (e.target) e.target.value = '';
    }
  };

  const handlePlacementImageUpload = async (placementKey, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const slot = placementFields[placementKey];
    if (!slot?.enabled) {
      setAlertMessage((prev) => ({
        ...prev,
        error: 'Turn on “Use this placement” before uploading an image.',
        success: ''
      }));
      if (e.target) e.target.value = '';
      return;
    }
    const safeName = ensureAssetNameForUpload();
    if (!safeName) return;

    setUploadingPlacementKey(placementKey);
    try {
      const ext = file.name.split('.').pop();
      const objectKey = `${S3_ADS_FOLDER}/placements/${safeName}/${placementKey}/image.${ext}`;
      const location = await uploadBannerFileViaPresignedUrl(file, objectKey);
      setPlacementFields((prev) => {
        const cur = prev[placementKey] || { image_url: '', redirection_url: '', enabled: false };
        return {
          ...prev,
          [placementKey]: { ...cur, image_url: location, enabled: true }
        };
      });
      setAlertMessage((prev) => ({ ...prev, success: `Uploaded ${placementKey} image`, error: '' }));
    } catch (err) {
      console.error(err);
      setAlertMessage((prev) => ({
        ...prev,
        error: err?.message || `Failed to upload ${placementKey} image`,
        success: ''
      }));
    } finally {
      setUploadingPlacementKey(null);
      if (e.target) e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const incompleteEnabled = BANNER_PLACEMENT_KEYS.some((k) => {
      const p = placementFields[k];
      if (!p?.enabled) return false;
      return !(p.image_url || '').trim() || !(p.redirection_url || '').trim();
    });
    if (incompleteEnabled) {
      setAlertMessage((prev) => ({
        ...prev,
        error: 'Each placement you enable must have both an image URL and a redirection URL (or turn the placement off).',
        success: ''
      }));
      return;
    }
    const placementsPayload = buildPlacementsPayload(placementFields);
    if (Object.keys(placementsPayload).length < 1) {
      setAlertMessage((prev) => ({
        ...prev,
        error: 'Enable at least one placement and add both image and redirection URL for it (default row media is not used for placements).',
        success: ''
      }));
      return;
    }
    if (!formData.adSetId || !formData.assetName.trim() || !formData.media.trim() || !formData.ctaUrl.trim()) {
      setAlertMessage((prev) => ({
        ...prev,
        error: 'Ad set, asset name, default media, and CTA URL are required.',
        success: ''
      }));
      return;
    }

    const baseBody = {
      ad_set_id: parseInt(formData.adSetId, 10),
      asset_name: formData.assetName.trim(),
      caption: formData.caption ?? '',
      media: formData.media.trim(),
      cta_text: formData.ctaText ?? '',
      cta_url: formData.ctaUrl.trim(),
      priority: parseInt(formData.priority, 10),
      placements: placementsPayload
    };

    try {
      const response = selectedAd
        ? await bannerAdsApi.update(selectedAd.id, baseBody)
        : await bannerAdsApi.create(baseBody);

      if (response.statusCode === '10000') {
        setAlertMessage((prev) => ({
          ...prev,
          success: response.message || `Banner ad ${selectedAd ? 'updated' : 'created'} successfully`,
          error: ''
        }));
        handleCloseModal();
        fetchBannerAds();
      } else {
        setAlertMessage((prev) => ({
          ...prev,
          error: response.message || `Failed to ${selectedAd ? 'update' : 'create'} banner ad`,
          success: ''
        }));
      }
    } catch (err) {
      console.error(err);
      setAlertMessage((prev) => ({
        ...prev,
        error: `Error ${selectedAd ? 'updating' : 'creating'} banner ad`,
        success: ''
      }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner ad?')) return;
    try {
      const response = await bannerAdsApi.delete(id);
      if (response.statusCode === '10000') {
        setAlertMessage((prev) => ({
          ...prev,
          success: response.message || 'Banner ad deleted',
          error: ''
        }));
        fetchBannerAds();
      } else {
        setAlertMessage((prev) => ({
          ...prev,
          error: response.message || 'Failed to delete banner ad',
          success: ''
        }));
      }
    } catch (e) {
      console.error(e);
      setAlertMessage((prev) => ({ ...prev, error: 'Error deleting banner ad', success: '' }));
    }
  };

  const filtered = Array.isArray(ads)
    ? ads.filter((ad) => {
        const q = searchTerm.toLowerCase();
        return (
          ad.assetName.toLowerCase().includes(q) ||
          String(ad.adSetId).includes(q)
        );
      })
    : [];

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="banner-ads-container banner-ads-page">
      <SetAlert alertMessage={alertMessage} setAlertMessage={setAlertMessage} />
      <Card className="banner-ads-list-card">
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <h4 className="mb-0">Banner ads</h4>
            <small className="text-muted">Multi-placement creatives · API <code>/banner-ads/ads</code></small>
          </div>
          <Button
            className="fw-semibold px-3"
            style={{
              background: 'linear-gradient(135deg, #0d9488, #0f766e)',
              border: 'none',
              borderRadius: '8px'
            }}
            onClick={() => handleShowModal()}
          >
            <FaPlus className="me-2" />
            New banner ad
          </Button>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={12} lg={8}>
              <InputGroup className="banner-search">
                <InputGroup.Text style={{ background: '#f8fafc', borderColor: '#cbd5e1' }}>
                  <FaSearch className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by asset name or ad set id..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ borderColor: '#cbd5e1' }}
                />
              </InputGroup>
            </Col>
          </Row>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Asset</th>
                <th>Ad set</th>
                <th>Priority</th>
                <th>Placements</th>
                <th>Default media</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ad) => (
                <tr key={ad.id}>
                  <td>{ad.assetName}</td>
                  <td>
                    {adSets.find((s) => s.id === ad.adSetId)?.adSetName || ad.adSetId}
                  </td>
                  <td>{ad.priority}</td>
                  <td>
                    <Badge bg="info">{countActivePlacementsFromApi(ad.placements)} active</Badge>
                  </td>
                  <td>
                    {ad.media && (
                      <img
                        src={ad.media}
                        alt=""
                        style={{ width: 48, height: 48, objectFit: 'contain' }}
                      />
                    )}
                  </td>
                  <td>
                    <Badge bg={ad.status === 'active' ? 'success' : 'secondary'}>{ad.status}</Badge>
                  </td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleShowModal(ad)}
                    >
                      <FaEdit />
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(ad.id)}>
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        scrollable
        dialogClassName="banner-ads-modal"
        contentClassName="border-0"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-2"
              style={{
                width: 40,
                height: 40,
                background: 'rgba(236, 253, 245, 0.25)',
                border: '1px solid rgba(204, 251, 241, 0.35)'
              }}
            >
              <FaImage />
            </span>
            {selectedAd ? 'Edit banner ad' : 'New banner ad'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="p-0">
            <div className="banner-form-scroll">
              {(alertMessage.error || alertMessage.success) && (
                <Alert
                  variant={alertMessage.error ? 'danger' : 'success'}
                  dismissible
                  onClose={() => setAlertMessage({ success: '', error: '' })}
                  className="mb-3"
                >
                  {alertMessage.error || alertMessage.success}
                </Alert>
              )}
              <div className="banner-form-callout">
                <strong>Banner vs feed:</strong> feed creatives live under <strong>Ads</strong>. This form targets{' '}
                <code>/banner-ads/ads</code> with per-placement images. uploads use{' '}
                <code>POST /admin/ads/presigned-url</code> (no AWS keys in the browser). Row-level
                <strong>default media + CTA</strong> are still required API fallbacks.
              </div>

              <div className="banner-form-section">
                <div className="banner-form-section-header">
                  <div className="banner-form-section-icon banner-form-section-icon--identity">
                    <FaLayerGroup />
                  </div>
                  <div>
                    <h3 className="banner-form-section-title">Campaign link & identity</h3>
                    <p className="banner-form-section-sub">
                      Tie this pack to an ad set, set internal name (used in upload paths), priority, and optional caption.
                    </p>
                  </div>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Ad set</Form.Label>
                  <Form.Select
                    name="adSetId"
                    value={formData.adSetId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select ad set</option>
                    {adSets.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.adSetName}
                        {s.brandName ? ` — ${s.brandName}` : ''}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Asset name</Form.Label>
                  <Form.Control
                    type="text"
                    name="assetName"
                    value={formData.assetName}
                    onChange={handleInputChange}
                    required
                    placeholder="Internal name (used in S3 paths)"
                  />
                </Form.Group>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Priority</Form.Label>
                      <Form.Control
                        type="number"
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                        min={1}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-0">
                  <Form.Label>Caption (optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="caption"
                    value={formData.caption}
                    onChange={handleInputChange}
                    rows={2}
                  />
                </Form.Group>
              </div>

              <div className="banner-form-section banner-form-section--fallback">
                <div className="banner-form-section-header">
                  <div className="banner-form-section-icon banner-form-section-icon--fallback">
                    <FaImages />
                  </div>
                  <div>
                    <h3 className="banner-form-section-title">Default fallback (required)</h3>
                    <p className="banner-form-section-sub">
                      API expects these when a placement does not supply its own creative. Highlighted so they are not
                      skipped.
                    </p>
                  </div>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Default image</Form.Label>
                  <div className="d-flex align-items-center gap-2 flex-wrap p-2 rounded-2" style={{ background: '#fffbeb', border: '1px dashed #fbbf24' }}>
                    {formData.media && (
                      <img
                        src={formData.media}
                        alt=""
                        style={{ width: 52, height: 52, objectFit: 'contain', borderRadius: 8, border: '1px solid #e2e8f0' }}
                      />
                    )}
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleDefaultMediaUpload}
                      disabled={uploadingMedia}
                      style={{ flex: '1 1 200px' }}
                    />
                    {uploadingMedia && (
                      <div className="spinner-border spinner-border-sm text-warning" role="status" />
                    )}
                  </div>
                  <Form.Control
                    type="url"
                    name="media"
                    className="mt-2"
                    value={formData.media}
                    onChange={handleInputChange}
                    placeholder="https://..."
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>CTA text (optional)</Form.Label>
                  <Form.Control type="text" name="ctaText" value={formData.ctaText} onChange={handleInputChange} />
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label>Default landing URL</Form.Label>
                  <Form.Control
                    type="url"
                    name="ctaUrl"
                    value={formData.ctaUrl}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </div>

              <div className="banner-form-section banner-form-section--placements">
                <div className="banner-form-section-header">
                  <div className="banner-form-section-icon banner-form-section-icon--placements">
                    <FaTh />
                  </div>
                  <div>
                    <h3 className="banner-form-section-title">Where this banner appears</h3>
                    <p className="banner-form-section-sub">
                      Main control: toggle <strong>Use this placement</strong>, then add image + redirect per slot.
                      Disabled slots are omitted on save.
                    </p>
                  </div>
                </div>
                <div className="banner-placements-intro">
                  Turn on placements only where you want delivery. Each enabled slot needs its own image and URL —
                  <strong> default row media is not used on those placements.</strong> Turning off clears this slot’s
                  creative; your default image on the row stays on the server.
                </div>
                {BANNER_PLACEMENT_KEYS.map((key) => {
                  const slot = placementFields[key] || {};
                  const placementOn = Boolean(slot.enabled);
                  return (
                    <Card
                      key={key}
                      className={`mb-3 banner-placement-card ${placementOn ? 'banner-placement-card--active' : 'banner-placement-card--inactive'}`}
                    >
                      <Card.Header className="py-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <span className="banner-placement-key">{key}</span>
                          <span className="banner-placement-label">{BANNER_PLACEMENT_LABELS[key]}</span>
                        </div>
                        <Form.Check
                          type="switch"
                          id={`placement-enabled-${key}`}
                          label="Use this placement"
                          checked={placementOn}
                          onChange={(e) => handlePlacementEnabledChange(key, e.target.checked)}
                          className="m-0"
                        />
                      </Card.Header>
                      <Card.Body className="py-3">
                        {!placementOn ? (
                          <p className="banner-placement-disabled-msg mb-0">
                            Off — no creative for this placement. Not served until you enable and add image + URL.
                          </p>
                        ) : (
                          <Row className="g-2">
                            <Col md={12}>
                              <Form.Label className="small">Image</Form.Label>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                {slot.image_url && (
                                  <img
                                    src={slot.image_url}
                                    alt=""
                                    style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 8, border: '1px solid #e2e8f0' }}
                                  />
                                )}
                                <Form.Control
                                  type="file"
                                  accept="image/*"
                                  disabled={uploadingPlacementKey === key}
                                  onChange={(e) => handlePlacementImageUpload(key, e)}
                                />
                                {uploadingPlacementKey === key && (
                                  <div
                                    className="spinner-border spinner-border-sm"
                                    style={{ color: '#0d9488' }}
                                    role="status"
                                  />
                                )}
                              </div>
                              <Form.Control
                                type="url"
                                className="mt-2"
                                placeholder="Image URL"
                                value={slot.image_url || ''}
                                onChange={(e) => handlePlacementFieldChange(key, 'image_url', e.target.value)}
                              />
                            </Col>
                            <Col md={12}>
                              <Form.Label className="small mt-2">Redirection URL</Form.Label>
                              <Form.Control
                                type="url"
                                placeholder="https://…"
                                value={slot.redirection_url || ''}
                                onChange={(e) => handlePlacementFieldChange(key, 'redirection_url', e.target.value)}
                              />
                            </Col>
                          </Row>
                        )}
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" type="button" onClick={handleCloseModal} className="px-3">
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedAd ? 'Save changes' : 'Create banner ad'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default BannerAds;
